import { BadgePercent, Heart, Minus, Plus, Search, Trash2 } from "lucide-react";
import { useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { CartItem } from "../../../../Redux/custmoer_cart_service/types";
import { ProductType } from "../../../../Redux/custmoer_cart_service/types";
import MobileCartOffersSheet from "../MobileCartOffersSheet";

type CartPromoValues = Record<string, string>;
type CartPromoApplied = Record<string, boolean>;

type CartItemsSectionProps = {
    cartItems: CartItem[];
    selectedItems: string[];
    selectedCartItemsCount: number;
    allSelected: boolean;
    itemTotal: number;
    toggleSelectAll: () => void;
    toggleItem: (itemId: string) => void;
    handleDecrease: (item: CartItem) => void;
    increaseQuantity: (itemId: string) => void;
    handleDelete: (itemId: string) => void;
    handleSaveForLater: (item: CartItem) => void;
    voucherInputs: CartPromoValues;
    setVoucherInputs: Dispatch<SetStateAction<CartPromoValues>>;
    referralInputs: CartPromoValues;
    setReferralInputs: Dispatch<SetStateAction<CartPromoValues>>;
    salarCodeInputs: CartPromoValues;
    setSalarCodeInputs: Dispatch<SetStateAction<CartPromoValues>>;
    voucherApplied: CartPromoApplied;
    setVoucherApplied: Dispatch<SetStateAction<CartPromoApplied>>;
    referralApplied: CartPromoApplied;
    setReferralApplied: Dispatch<SetStateAction<CartPromoApplied>>;
    salarCodeApplied: CartPromoApplied;
    setSalarCodeApplied: Dispatch<SetStateAction<CartPromoApplied>>;
    applyVoucher: (itemId: string) => void;
    applyReferral: (itemId: string) => void;
    applySalarCode: (itemId: string) => void;
    removeReferral?: () => void;
    removeSalarCode?: () => void;
    appliedInvitationCode?: string | null;
    appliedSalarCode?: string | null;
    appliedVoucherAmount?: number;
};

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(value)));

const CartItemsSection = ({
    cartItems,
    selectedItems,
    selectedCartItemsCount,
    allSelected,
    itemTotal,
    toggleSelectAll,
    toggleItem,
    handleDecrease,
    increaseQuantity,
    handleDelete,
    handleSaveForLater,
    voucherInputs,
    setVoucherInputs,
    referralInputs,
    setReferralInputs,
    salarCodeInputs,
    setSalarCodeInputs,
    voucherApplied,
    setVoucherApplied,
    referralApplied,
    setReferralApplied,
    salarCodeApplied,
    setSalarCodeApplied,
    applyVoucher,
    applyReferral,
    applySalarCode,
    removeReferral,
    removeSalarCode,
    appliedInvitationCode,
    appliedSalarCode,
    appliedVoucherAmount,
}: CartItemsSectionProps) => {
    const navigate = useNavigate();

    const [activeOfferItemId, setActiveOfferItemId] = useState<string | null>(null);

    const activeOfferItem = cartItems.find((item) => item.id === activeOfferItemId) || null;

    const getAppliedOfferCount = (itemId: string) =>
        [voucherApplied[itemId], referralApplied[itemId], salarCodeApplied[itemId]].filter(Boolean).length;

    const handleIncrease = (item: CartItem) => {
        const isMlmItem = Boolean(
            item.isMlmProduct ||
            (item as any).isMLMProduct ||
            item.productType === ProductType.MLM_VOUCHER
        );

        if (isMlmItem && item.quantity >= 1) {
            toast.warning("You can only purchase 1 quantity of this referral package at a time.");
            return;
        }

        increaseQuantity(item.id);
    };

    return (
        <>
            <div className="cart-heading">
                <div>
                    <span>YOUR CART</span>
                    <h1>Shopping Cart</h1>
                    <p>
                        {selectedCartItemsCount} of {cartItems.length} items selected
                    </p>
                </div>

                <div className="cart-heading-price">Price</div>
            </div>

            {cartItems.length > 0 && (
                <div className="cart-select-row">
                    <label>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        <span>
                            {allSelected ? "Deselect all items" : "Select all items"}
                        </span>
                    </label>

                    <span>Select products to include them in checkout</span>
                </div>
            )}

            <div className="cart-items-list">
                {cartItems.map((item) => {
                    const isSelected = selectedItems.includes(item.id);
                    const mrp = item.metadata?.mrp ?? item.unitPrice;
                    const discount =
                        mrp > item.unitPrice
                            ? Math.round(((mrp - item.unitPrice) / mrp) * 100)
                            : 0;

                    const isMlmItem = Boolean(
                        item.isMlmProduct ||
                        (item as any).isMLMProduct ||
                        item.productType === ProductType.MLM_VOUCHER
                    );

                    const isInStock = item.metadata?.inStock !== false;
                    const isVoucherActive = Boolean(
                        voucherApplied[item.id] || (appliedVoucherAmount && appliedVoucherAmount > 0)
                    );
                    const isReferralActive = Boolean(
                        appliedInvitationCode || referralApplied[item.id]
                    );
                    const isSalarCodeActive = Boolean(
                        appliedSalarCode || salarCodeApplied[item.id]
                    );

                    return (
                        <article
                            className={`cart-item ${isSelected ? "cart-item-selected selected" : ""}`}
                            key={item.id}
                        >
                            <div className="cart-item-check">
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleItem(item.id)}
                                />
                            </div>

                            <div className="cart-product-image">
                                <img
                                    src={item.productImage ?? undefined}
                                    alt={item.productName}
                                />
                            </div>

                            <div className="cart-product-content">
                                <div className="cart-product-title-row">
                                    <div>
                                        <h2>{item.productName}</h2>
                                        {item.metadata?.description ? (
                                            <p className="cart-product-description">
                                                {item.metadata.description}
                                            </p>
                                        ) : (
                                            <p className="cart-product-description cart-product-description-mobile-fallback">
                                                Selected item from your Salar cart. Review the product details and chosen options before checkout.
                                            </p>
                                        )}
                                    </div>
                                    <strong className="cart-mobile-price">
                                        ₹{formatPrice(item.unitPrice * item.quantity)}
                                        </strong>
                                </div>

                                <span
                                    className="cart-stock"
                                    style={!isInStock ? { color: "#dc2626" } : undefined}
                                >
                                    {isInStock ? "In stock" : "Out of stock"}
                                </span>

                                <div className="cart-seller">
                                    Sold by:{" "}
                                    <strong>
                                        {item.sellerName || "Salar Retail"}
                                    </strong>
                                </div>

                                {/* Color / size / variant from metadata or variantName */}
                                {(item.metadata?.color ||
                                    item.metadata?.size ||
                                    item.variantName) && (
                                    <div className="cart-options-info">
                                        {item.metadata?.color && (
                                            <span>
                                                Colour <strong>{item.metadata.color}</strong>
                                            </span>
                                        )}
                                        {item.metadata?.size && (
                                            <span>
                                                Size <strong>{item.metadata.size}</strong>
                                            </span>
                                        )}
                                        {item.variantName && (
                                            <span>
                                                Variant <strong>{item.variantName}</strong>
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="cart-product-actions">
                                    <div className="cart-quantity">
                                        <button
                                            type="button"
                                            onClick={() => handleDecrease(item)}
                                        >
                                            {item.quantity === 1 ? (
                                                <Trash2 size={14} />
                                            ) : (
                                                <Minus size={14} />
                                            )}
                                        </button>

                                        <span>{item.quantity}</span>

                                        <button
                                            type="button"
                                            disabled={isMlmItem && item.quantity >= 1}
                                            className={isMlmItem && item.quantity >= 1 ? "cart-qty-disabled" : ""}
                                            title={
                                                isMlmItem && item.quantity >= 1
                                                    ? "Max 1 quantity allowed for referral products"
                                                    : "Increase quantity"
                                            }
                                            onClick={() => handleIncrease(item)}
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        className={`cart-mobile-offers-trigger ${getAppliedOfferCount(item.id) > 0 ? "has-offers" : ""}`}
                                        onClick={() => setActiveOfferItemId(item.id)}
                                    >
                                        <BadgePercent size={14} strokeWidth={2} />
                                        <span>Apply offers</span>
                                        {getAppliedOfferCount(item.id) > 0 && (
                                            <strong>{getAppliedOfferCount(item.id)}</strong>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className="cart-text-action danger"
                                        onClick={() => handleDelete(item.id)}
                                    >
                                        Delete
                                    </button>

                                    <button
                                        type="button"
                                        className="cart-text-action"
                                        onClick={() => handleSaveForLater(item)}
                                    >
                                        <Heart size={13} strokeWidth={1.9} />
                                        Save for later
                                    </button>

                                    <button
                                        type="button"
                                        className="cart-text-action"
                                        onClick={() =>
                                            navigate(
                                                "/DetailPage/" + encodeURIComponent(item.productId)
                                            )
                                        }
                                    >
                                        See similar
                                    </button>
                                </div>

                                <div className="cart-benefit-strip">
                                    <span>Salar verified</span>
                                    <span>Secure checkout</span>
                                    <span>Easy returns</span>
                                </div>

                                <div className="cart-item-offers">
                                    <div className="cart-promos">
                                        {/* Shopping Voucher */}
                                        <div
                                            className={`cart-promo-field ${isVoucherActive ? "applied" : ""}`}
                                        >
                                            <input
                                                type="text"
                                                value={voucherInputs[item.id] || ""}
                                                onChange={(event) => {
                                                    setVoucherInputs((prev) => ({
                                                        ...prev,
                                                        [item.id]: event.target.value,
                                                    }));
                                                    setVoucherApplied((prev) => ({
                                                        ...prev,
                                                        [item.id]: false,
                                                    }));
                                                }}
                                                placeholder="Shopping Voucher"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => applyVoucher(item.id)}
                                            >
                                                {isVoucherActive ? "Applied" : "Apply"}
                                            </button>
                                        </div>

                                        {/* Referral / Invitation Code */}
                                        <div
                                            className={`cart-promo-field ${isReferralActive ? "applied" : ""}`}
                                        >
                                            <input
                                                type="text"
                                                value={
                                                    appliedInvitationCode ||
                                                    referralInputs[item.id] ||
                                                    ""
                                                }
                                                disabled={Boolean(appliedInvitationCode)}
                                                onChange={(event) => {
                                                    setReferralInputs((prev) => ({
                                                        ...prev,
                                                        [item.id]: event.target.value.toUpperCase(),
                                                    }));
                                                    setReferralApplied((prev) => ({
                                                        ...prev,
                                                        [item.id]: false,
                                                    }));
                                                }}
                                                placeholder="Enter Referral ID (e.g. S12A345)"
                                            />
                                            {appliedInvitationCode ? (
                                                <button
                                                    type="button"
                                                    className="cart-promo-remove"
                                                    onClick={removeReferral}
                                                    title="Remove Referral Code"
                                                >
                                                    Remove
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => applyReferral(item.id)}
                                                >
                                                    {referralApplied[item.id] ? "Applied" : "Apply"}
                                                </button>
                                            )}
                                        </div>

                                        {/* Salar Code */}
                                        <div
                                            className={`cart-promo-field ${isSalarCodeActive ? "applied" : ""}`}
                                        >
                                            <input
                                                type="text"
                                                value={
                                                    appliedSalarCode ||
                                                    salarCodeInputs[item.id] ||
                                                    ""
                                                }
                                                disabled={Boolean(appliedSalarCode)}
                                                onChange={(event) => {
                                                    setSalarCodeInputs((prev) => ({
                                                        ...prev,
                                                        [item.id]: event.target.value.toUpperCase(),
                                                    }));
                                                    setSalarCodeApplied((prev) => ({
                                                        ...prev,
                                                        [item.id]: false,
                                                    }));
                                                }}
                                                placeholder="Enter Salar Code"
                                            />
                                            {appliedSalarCode ? (
                                                <button
                                                    type="button"
                                                    className="cart-promo-remove"
                                                    onClick={removeSalarCode}
                                                    title="Remove Salar Code"
                                                >
                                                    Remove
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => applySalarCode(item.id)}
                                                >
                                                    {salarCodeApplied[item.id] ? "Applied" : "Apply"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="cart-mobile-bottom-actions">
                                <button
                                    type="button"
                                    className="danger"
                                    onClick={() => handleDelete(item.id)}
                                >
                                    <Trash2 size={13} strokeWidth={1.9} />
                                    <span>Delete</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleSaveForLater(item)}
                                >
                                    <Heart size={13} strokeWidth={1.9} />
                                    <span>Save for later</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate("/DetailPage/" + encodeURIComponent(item.productId))
                                    }
                                >
                                    <Search size={13} strokeWidth={1.9} />
                                    <span>See similar</span>
                                </button>
                            </div>

                            <div className="cart-product-price">
                                <strong>
                                    ₹{formatPrice(item.unitPrice * item.quantity)}
                                </strong>
                                <div>
                                    {mrp > item.unitPrice && (
                                        <del>₹{formatPrice(mrp * item.quantity)}</del>
                                    )}
                                    {discount > 0 && <span>{discount}% OFF</span>}
                                </div>
                                <small>Inclusive of available offers</small>
                            </div>
                        </article>
                    );
                })}
            </div>

            {activeOfferItem ? (
                <MobileCartOffersSheet
                    itemId={activeOfferItem.id}
                    itemTitle={activeOfferItem.productName}
                    voucherValue={voucherInputs[activeOfferItem.id] || ""}
                    referralValue={referralInputs[activeOfferItem.id] || ""}
                    salarCodeValue={salarCodeInputs[activeOfferItem.id] || ""}
                    voucherApplied={Boolean(voucherApplied[activeOfferItem.id])}
                    referralApplied={Boolean(referralApplied[activeOfferItem.id])}
                    salarCodeApplied={Boolean(salarCodeApplied[activeOfferItem.id])}
                    appliedInvitationCode={appliedInvitationCode || null}
                    appliedSalarCode={appliedSalarCode || null}
                    appliedVoucherAmount={appliedVoucherAmount || 0}
                    onVoucherChange={(value) => {
                        setVoucherInputs((prev) => ({ ...prev, [activeOfferItem.id]: value }));
                        setVoucherApplied((prev) => ({ ...prev, [activeOfferItem.id]: false }));
                    }}
                    onReferralChange={(value) => {
                        setReferralInputs((prev) => ({
                            ...prev,
                            [activeOfferItem.id]: value.toUpperCase(),
                        }));
                        setReferralApplied((prev) => ({ ...prev, [activeOfferItem.id]: false }));
                    }}
                    onSalarCodeChange={(value) => {
                        setSalarCodeInputs((prev) => ({
                            ...prev,
                            [activeOfferItem.id]: value.toUpperCase(),
                        }));
                        setSalarCodeApplied((prev) => ({ ...prev, [activeOfferItem.id]: false }));
                    }}
                    onApplyVoucher={() => applyVoucher(activeOfferItem.id)}
                    onApplyReferral={() => applyReferral(activeOfferItem.id)}
                    onApplySalarCode={() => applySalarCode(activeOfferItem.id)}
                    onRemoveReferral={() => removeReferral?.()}
                    onRemoveSalarCode={() => removeSalarCode?.()}
                    onClose={() => setActiveOfferItemId(null)}
                />
            ) : null}

            {cartItems.length > 0 && (
                <div className="cart-subtotal-line">
                    <span>Subtotal ({selectedCartItemsCount} selected)</span>
                    <strong>₹{formatPrice(itemTotal)}</strong>
                </div>
            )}
        </>
    );
};

export default CartItemsSection;

